import type { CompareRoundupConfig } from "@/components/compare-landing/roundup-types";

/**
 * Config for the `/compare/empower-alternatives` roundup, consumed by
 * `RoundupLandingPage`. Lives outside the component module so the page file
 * only exports components (react-refresh rule) — strings resolve via the
 * `compareEmpowerAlternatives.*` namespace in each locale's
 * `i18n/<locale>/compare/empower-alternatives.json`.
 *
 * `apps` order is the published ranking and must match `APP_KEYS` in
 * `compare-empower-alternatives.meta.ts`; `compare-roundup-manifest.test.ts`
 * pins them.
 *
 * The matrix rows are this audience's, not a sibling roundup's: readers arrive
 * from a *net-worth and investment* tracker, so the questions are what it costs,
 * whether the vendor sells advice, what can count as an asset, how much
 * investment detail survives, whether it projects, and who holds the record.
 */
export const empowerAlternativesConfig: CompareRoundupConfig = {
  keyPrefix: "compareEmpowerAlternatives",
  hero: {
    webShotSrc: "/img/ledger/overview-example.webp",
    trustKeys: ["trust1", "trust2", "trust3"],
  },
  apps: [
    {
      key: "beancount",
      icon: "lock",
      isBeancount: true,
      prosKeys: ["p1", "p2", "p3"],
      consKeys: ["c1", "c2"],
    },
    {
      key: "kubera",
      icon: "coins",
      prosKeys: ["p1", "p2", "p3"],
      consKeys: ["c1", "c2"],
    },
    {
      key: "projectionlab",
      icon: "trending-up",
      prosKeys: ["p1", "p2", "p3"],
      consKeys: ["c1", "c2"],
    },
    {
      key: "sharesight",
      icon: "graduation-cap",
      prosKeys: ["p1", "p2", "p3"],
      consKeys: ["c1", "c2"],
    },
    {
      key: "monarch",
      icon: "layout-dashboard",
      prosKeys: ["p1", "p2", "p3"],
      consKeys: ["c1", "c2"],
    },
    {
      key: "simplifi",
      icon: "target",
      prosKeys: ["p1", "p2", "p3"],
      consKeys: ["c1", "c2"],
    },
  ],
  matrix: {
    rows: [
      "cost",
      "salesPitch",
      "assetCoverage",
      "costBasis",
      "projections",
      "ownership",
    ],
  },
  related: [
    { slug: "beancount-vs-monarch", key: "monarch" },
    { slug: "monarch-alternatives", key: "monarchAlternatives" },
    {
      to: "/tools/csv-to-beancount/empower",
      key: "empowerCsv",
      ctaKey: "toolCta.action",
    },
  ],
};
